import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { IonIcon, Platform } from '@ionic/angular/standalone';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { addIcons } from 'ionicons';
import {
  checkmarkCircleOutline,
  chevronBackOutline,
  businessOutline,
} from 'ionicons/icons';
import { BtnComponent } from '../btn/btn.component';
import { V2AccordionComponent, V2AccordionPreviewSticker } from '../v2-accordion/v2-accordion.component';
import { PopupService } from '../../providers/popup.service';
import { StickerDetailOverlayService } from '../../providers/sticker-detail-overlay.service';
import { TradeRequestOverlayAction } from '../../providers/trade-request-detail-overlay.service';
import {
  buildTradePreferencesPopupHtml,
  buildTradeStickerOverlaySections,
  formatTradeRequestDate,
  formatTradeRequestRelativeDate,
  getGiveItems,
  getPreferredTradeLocations,
  getReceiveItems,
  getTradeDisplayEntries,
  isTradeRepeatedItem,
  getTradeRequestCloseLabelKey,
  getTradeRequestDeclineLabelKey,
  getTradeRequestStatusClass,
  getTradeRequestStatusKey,
  getTradeStickerImgSrc,
  hasPreferredTradeLocations,
  mapTradeItemsToOverlayItems,
  shouldShowTradeAcceptBtn,
  shouldShowTradeCloseBtn,
  shouldShowTradeDeclineBtn,
  showMailTradeHelp,
  showNonVTradeMetadata,
} from '../../pages/user-collection/trade-requests/trade-request-items.util';

type TradeStickerEntryView = {
  item: any;
  qty: number;
  imgSrc: string;
  imgKey: string;
  landscape: boolean;
  repeated: boolean;
  previewSide: 'give' | 'receive';
};

type TradeStickerSectionView = {
  key: string;
  side: 'give' | 'receive';
  titleKey: string;
  itemCount: number;
  entries: TradeStickerEntryView[];
  previewStickers: V2AccordionPreviewSticker[];
};

type TradeOtherColView = {
  collectionId: number | string;
  collectionName: string;
  collectionIcon?: string;
  entries: TradeStickerEntryView[];
  previewStickers: V2AccordionPreviewSticker[];
};

const trdScrollTopFab = trigger('trdScrollTopFab', [
  transition(':enter', [
    style({ opacity: 0, transform: 'translate3d(0, 14px, 0) scale(0.86)' }),
    animate(
      '280ms cubic-bezier(0.22, 1, 0.36, 1)',
      style({ opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' })
    ),
  ]),
  transition(':leave', [
    animate(
      '200ms cubic-bezier(0.4, 0, 1, 1)',
      style({ opacity: 0, transform: 'translate3d(0, 10px, 0) scale(0.9)' })
    ),
  ]),
]);

@Component({
  selector: 'app-v2-trade-request-detail-overlay',
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    NgTemplateOutlet,
    TranslatePipe,
    IonIcon,
    BtnComponent,
    V2AccordionComponent,
  ],
  templateUrl: './v2-trade-request-detail-overlay.component.html',
  styleUrls: ['./v2-trade-request-detail-overlay.component.scss'],
  animations: [trdScrollTopFab],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V2TradeRequestDetailOverlayComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() request: any = null;
  @Input() collectionType = '';
  @Input() collectionId = '';
  @Input() agentId = '';
  @Input() userCollectionDetails: any = null;
  @Input() readOnly = false;
  @Input() listNav: '' | 'Received' | 'Sent' | 'History' = '';

  @Output() closed = new EventEmitter<void>();
  @Output() actionRequested = new EventEmitter<TradeRequestOverlayAction>();

  @ViewChild('bodyScroll') bodyScrollRef?: ElementRef<HTMLElement>;
  @ViewChildren(V2AccordionComponent)
  accordionRefs?: QueryList<V2AccordionComponent>;

  domOpen = false;
  isClosing = false;
  interactionReady = false;

  primarySections: TradeStickerSectionView[] = [];
  otherColSections: TradeOtherColView[] = [];
  declineLabelKey = 'CANCEL_REQUEST';
  defaultExpandedPrimaryKey: 'receive' | 'give' = 'receive';
  isAtTop = true;

  private readonly scrollThreshold = 120;

  private closeAnimationFinishTimer: ReturnType<typeof setTimeout> | null = null;
  private interactionReadyTimer: ReturnType<typeof setTimeout> | null = null;
  private imageLoadSyncTimers: ReturnType<typeof setTimeout>[] = [];
  private static readonly OVERLAY_CLOSE_ANIM_MS = 400;
  private static readonly INTERACTION_GUARD_MS = 400;
  private static readonly IMAGE_LOAD_SYNC_DELAYS_MS = [0, 50, 150, 400, 800, 1500];

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly popup: PopupService,
    private readonly stickerOverlay: StickerDetailOverlayService,
    private readonly platform: Platform,
    public readonly translate: TranslateService
  ) {
    addIcons({
      checkmarkCircleOutline,
      chevronBackOutline,
      businessOutline,
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['request'] ||
      changes['collectionType'] ||
      changes['userCollectionDetails'] ||
      changes['readOnly'] ||
      changes['listNav']
    ) {
      this.rebuildViewModel();
      if (this.domOpen && this.request) {
        this.scheduleImageLoadSync();
      }
    }
    if (changes['isOpen']) {
      if (this.isOpen) {
        this.clearCloseAnimationFinishTimer();
        this.clearInteractionReadyTimer();
        this.isClosing = false;
        this.domOpen = true;
        this.interactionReady = false;
        this.isAtTop = true;
        this.rebuildViewModel();
        this.resetBodyScroll();
        this.setBodyScrollLock(true);
        this.scheduleInteractionReady();
        this.scheduleImageLoadSync();
      } else if (this.domOpen) {
        this.isClosing = true;
        this.scheduleCloseAnimationFinish();
      }
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.clearCloseAnimationFinishTimer();
    this.clearInteractionReadyTimer();
    this.clearImageLoadSyncTimers();
    this.setBodyScrollLock(false);
  }

  get partnerName(): string {
    return this.request?.TradePartnerAgentName ?? '';
  }

  get titlePillLabel(): string {
    const name = this.partnerName;
    if (!this.readOnly) {
      return name;
    }
    const relative = formatTradeRequestRelativeDate(this.request?.creation_time);
    return relative ? `${name} · ${relative}` : name;
  }

  get closeLabelKey(): string {
    return getTradeRequestCloseLabelKey();
  }

  get showTradeMetadata(): boolean {
    return showNonVTradeMetadata(this.request, this.collectionType);
  }

  get hasPreferredLocations(): boolean {
    return hasPreferredTradeLocations(this.request, this.collectionType);
  }

  get preferredLocations(): string[] {
    return getPreferredTradeLocations(this.request);
  }

  get showMailTrade(): boolean {
    return showMailTradeHelp(this.request, this.collectionType);
  }

  get showLocationsAction(): boolean {
    return this.showTradeMetadata;
  }

  get locationsActionLabel(): string {
    return this.hasPreferredLocations
      ? this.translate.instant('TARGET_TRADE_LOCATIONS')
      : this.translate.instant('ALLOW_TRADE_BY_MAIL');
  }

  trackSection(_index: number, section: TradeStickerSectionView): string {
    return section.key;
  }

  isPrimarySectionExpanded(section: TradeStickerSectionView): boolean {
    return section.key === this.defaultExpandedPrimaryKey;
  }

  isOtherColSectionExpanded(_section: TradeOtherColView): boolean {
    return false;
  }

  onBodyScroll(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }
    const atTop = target.scrollTop <= this.scrollThreshold;
    if (atTop !== this.isAtTop) {
      this.isAtTop = atTop;
      this.cdr.markForCheck();
    }
  }

  scrollBodyToTop(): void {
    const el = this.bodyScrollRef?.nativeElement;
    if (!el || el.scrollTop === 0) {
      return;
    }
    el.scrollTo({ top: 0, behavior: 'smooth' });
  }

  trackEntry(_index: number, entry: TradeStickerEntryView): string {
    return entry.imgKey;
  }

  trackOtherCol(_index: number, col: TradeOtherColView): string {
    return String(col.collectionId);
  }

  trackCollectionCol(_index: number, col: any): string {
    return String(col?.collection_id ?? _index);
  }

  revealImage(event: Event): void {
    const img = event.currentTarget as HTMLImageElement | null;
    if (!img || img.classList.contains('v2-trd-img--loaded')) {
      return;
    }
    img.classList.add('v2-trd-img--loaded');
    this.refreshAccordionHeights();
  }

  colImageKey(col: any): string {
    const src = col?.collection_icon_path;
    return `col:${col?.collection_id ?? 'unknown'}:${src ? String(src).trim() : ''}`;
  }

  get avatarImageKey(): string {
    const src = this.partnerAvatar;
    return `avatar:${src ? String(src).trim() : ''}`;
  }

  get partnerAvatar(): string {
    return (
      this.request?.TradePartnerAvatar ?? 'assets/images/logo-noletter.svg'
    );
  }

  get dateLabel(): string {
    return formatTradeRequestDate(this.request?.creation_time);
  }

  get statusKey(): string {
    return getTradeRequestStatusKey(this.request, { history: this.readOnly });
  }

  get statusClass(): string {
    return getTradeRequestStatusClass(this.request, { history: this.readOnly });
  }

  get isCancelled(): boolean {
    return this.request?.request_status === 'cancelled';
  }

  get giveItems(): any[] {
    return getGiveItems(this.request);
  }

  get receiveItems(): any[] {
    return getReceiveItems(this.request);
  }

  get otherColsCount(): number {
    return (this.request?.OtherCols ?? []).filter(
      (col: any) => col.collection_id !== this.request?.collection_id
    ).length;
  }

  get hasOtherCols(): boolean {
    return this.otherColsCount > 0;
  }

  get collectionIconPath(): string {
    return this.userCollectionDetails?.collection_icon_path ?? '';
  }

  get isVirtualCollection(): boolean {
    return this.collectionType === 'V';
  }

  isLandscapeItem(item: any): boolean {
    return String(item?.orientation ?? 'P').toUpperCase() === 'L';
  }

  isRepeatedItem(item: any): boolean {
    return isTradeRepeatedItem(item);
  }

  otherColItems(col: any): any[] {
    return (col?.ReqItemsDetailsAll ?? []).filter(
      (item: any) =>
        this.isOtherColReceiveItem(item, col) ||
        this.isOtherColGiveItem(item, col)
    );
  }

  resolveStickerImg(item: any, col: any | null): string {
    if (col) {
      return this.otherColStickerImg(item, col);
    }
    return this.stickerImg(item);
  }

  resolvePreviewSide(
    item: any,
    side: 'give' | 'receive' | null,
    col: any | null
  ): 'give' | 'receive' {
    if (side) {
      return side;
    }
    if (col && this.isOtherColReceiveItem(item, col)) {
      return 'receive';
    }
    return 'give';
  }

  get showAcceptBtn(): boolean {
    return shouldShowTradeAcceptBtn(this.request, this.readOnly);
  }

  get showCloseBtn(): boolean {
    return shouldShowTradeCloseBtn(this.request, this.readOnly);
  }

  get showDeclineBtn(): boolean {
    return shouldShowTradeDeclineBtn(this.request, this.readOnly);
  }

  get showWaitingBadge(): boolean {
    return (
      !this.readOnly &&
      this.request?.request_status_type === 'online' &&
      this.request?.is_valid == 1 &&
      this.request?.request_status === 'accepted'
    );
  }

  get showFooter(): boolean {
    return this.showAcceptBtn || this.showCloseBtn || this.showDeclineBtn;
  }

  stickerImg(item: any): string {
    return getTradeStickerImgSrc(
      item,
      this.collectionType,
      this.userCollectionDetails,
      this.collectionIconPath
    );
  }

  otherColStickerImg(item: any, col: any): string {
    if (this.collectionType === 'V') {
      return getTradeStickerImgSrc(
        item,
        this.collectionType,
        this.userCollectionDetails,
        this.collectionIconPath
      );
    }
    return col?.collection_icon_path || this.collectionIconPath;
  }

  isOtherColReceiveItem(item: any, col: any): boolean {
    return (
      (item.type === 'Receive' || item.type === 'Receive Full') &&
      col.collection_id !== this.request?.collection_id
    );
  }

  isOtherColGiveItem(item: any, col: any): boolean {
    return (
      (item.type === 'Give' || item.type === 'Give Full') &&
      col.collection_id !== this.request?.collection_id
    );
  }

  onClose(): void {
    this.closed.emit();
  }

  onOverlayCaptureClick(event: Event): void {
    if (this.interactionReady) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest('.v2-trd-floating-back')) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  onAccept(): void {
    this.actionRequested.emit('accept');
  }

  onDecline(): void {
    this.actionRequested.emit('decline');
  }

  openStickerPreview(item: any, side: 'give' | 'receive'): void {
    if (this.collectionType !== 'V') {
      this.popup.open({
        title: item?.item_name ?? '',
        message: item?.item_id ? `#${item.item_id}` : '',
      });
      return;
    }

    const giveLabel = this.translate.instant('ITEMS_TO_GIVE');
    const receiveLabel = this.translate.instant('ITEMS_TO_RECEIVE');
    const sections = buildTradeStickerOverlaySections(
      this.request,
      giveLabel,
      receiveLabel
    );
    const sectionLabel = side === 'give' ? giveLabel : receiveLabel;
    const sectionItems =
      side === 'receive'
        ? mapTradeItemsToOverlayItems(getReceiveItems(this.request))
        : mapTradeItemsToOverlayItems(getGiveItems(this.request));
    const sectionIndex = Math.max(
      0,
      sections.findIndex((section) => section.section_name === sectionLabel)
    );
    const selectedIndex = Math.max(
      0,
      sectionItems.findIndex((i) => i.item_id === item.item_id)
    );

    this.stickerOverlay.open({
      items: sectionItems,
      sections,
      currentSectionIndex: sectionIndex,
      selectedIndex,
      collectionDetails: this.userCollectionDetails,
      iconLocationPrefix:
        this.userCollectionDetails?.icon_location_prefix ?? '',
      showFooter: false,
      showOpenAlbumButton: false,
      showVideo: true,
      isMobile: this.platform.is('mobile'),
      callbacks: {
        onClosed: () => {},
      },
    });
  }

  clickRepeatedBadge(item: any): void {
    const type = String(item?.type ?? '')
      .trim()
      .toLowerCase();
    if (type === 'receive full') {
      this.popup.open({
        title: this.translate.instant('TITLE_TRADE_OPT_GREEN_ME'),
        message: this.translate.instant('MESSAGE_TRADE_OPT_GREEN_ME'),
      });
    }
    if (type === 'give full') {
      this.popup.open({
        title: this.translate.instant('TITLE_TRADE_OPT_GREEN_OTHER'),
        message: this.translate.instant('MESSAGE_TRADE_OPT_GREEN_OTHER'),
      });
    }
  }

  openOtherColInfo(col: any): void {
    this.popup.open({
      title: col.collection_name,
      message: this.translate.instant(
        this.collectionId == col.collection_id
          ? 'OPEN_COL_INFO_POPUP'
          : 'OPEN_OTHER_COL_INFO_POPUP'
      ),
    });
  }

  openWaitingInfo(): void {
    this.popup.open({
      title: this.translate.instant('CLOSE_REQUEST'),
      message: this.translate.instant('CLOSE_REQUEST_INFO'),
    });
  }

  openTradeLocationsHelp(): void {
    const locations = this.preferredLocations;
    if (!locations.length && !this.showMailTrade) {
      return;
    }

    const title = locations.length
      ? this.translate.instant('TARGET_TRADE_LOCATIONS').replace(/:\s*$/, '')
      : this.translate.instant('ALLOW_TRADE_BY_MAIL');

    this.popup.open({
      title,
      messageHtml: buildTradePreferencesPopupHtml(
        locations,
        this.showMailTrade,
        this.translate.instant('ALLOW_TRADE_BY_MAIL')
      ),
    });
  }

  private rebuildViewModel(): void {
    if (!this.request) {
      this.primarySections = [];
      this.otherColSections = [];
      this.defaultExpandedPrimaryKey = 'receive';
      this.declineLabelKey = 'CANCEL_REQUEST';
      return;
    }

    this.declineLabelKey = getTradeRequestDeclineLabelKey(
      this.request,
      this.listNav
    );

    const receiveEntries = this.buildEntries(
      getReceiveItems(this.request),
      'receive',
      null
    );
    const giveEntries = this.buildEntries(getGiveItems(this.request), 'give', null);
    const receiveSection: TradeStickerSectionView = {
      key: 'receive',
      side: 'receive',
      titleKey: 'ITEMS_TO_RECEIVE',
      itemCount: getReceiveItems(this.request).length,
      entries: receiveEntries,
      previewStickers: this.buildPreviewStickers(receiveEntries),
    };
    const giveSection: TradeStickerSectionView = {
      key: 'give',
      side: 'give',
      titleKey: 'ITEMS_TO_GIVE',
      itemCount: getGiveItems(this.request).length,
      entries: giveEntries,
      previewStickers: this.buildPreviewStickers(giveEntries),
    };
    this.primarySections = [receiveSection, giveSection];
    this.defaultExpandedPrimaryKey =
      receiveEntries.length > 0
        ? 'receive'
        : giveEntries.length > 0
          ? 'give'
          : 'receive';

    this.otherColSections = (this.request.OtherCols ?? [])
      .filter((col: any) => col.collection_id !== this.request.collection_id)
      .map((col: any) => {
        const entries = this.buildEntries(
          this.otherColItems(col),
          null,
          col
        );
        return {
          collectionId: col.collection_id,
          collectionName: col.collection_name,
          collectionIcon: col.collection_icon_path,
          entries,
          previewStickers: this.buildPreviewStickers(entries),
        };
      });

  }

  private buildPreviewStickers(
    entries: TradeStickerEntryView[]
  ): V2AccordionPreviewSticker[] {
    if (this.collectionType !== 'V') {
      return [];
    }
    return entries.slice(0, 3).map((entry) => ({
      imgSrc: entry.imgSrc,
      landscape: entry.landscape,
      imgKey: entry.imgKey,
    }));
  }

  private buildEntries(
    items: any[],
    side: 'give' | 'receive' | null,
    col: any | null
  ): TradeStickerEntryView[] {
    return getTradeDisplayEntries(items).map((entry) => {
      const imgSrc = col
        ? this.otherColStickerImg(entry.item, col)
        : this.stickerImg(entry.item);
      const itemId = entry.item?.item_id ?? 'unknown';
      const normalizedSrc = imgSrc ? String(imgSrc).trim() : '';
      return {
        item: entry.item,
        qty: entry.qty,
        imgSrc,
        imgKey: `sticker:${itemId}:${normalizedSrc}`,
        landscape: String(entry.item?.orientation ?? 'P').toUpperCase() === 'L',
        repeated: entry.repeated,
        previewSide: this.resolvePreviewSide(entry.item, side, col),
      };
    });
  }

  private scheduleImageLoadSync(): void {
    this.clearImageLoadSyncTimers();
    for (const delay of V2TradeRequestDetailOverlayComponent.IMAGE_LOAD_SYNC_DELAYS_MS) {
      const timer = setTimeout(() => this.syncCachedOverlayImages(), delay);
      this.imageLoadSyncTimers.push(timer);
    }
  }

  private syncCachedOverlayImages(): void {
    const root = this.bodyScrollRef?.nativeElement;
    if (!root) {
      return;
    }

    root
      .querySelectorAll<HTMLImageElement>('img[data-v2-trd-img-key]')
      .forEach((img) => {
        if (img.classList.contains('v2-trd-img--loaded')) {
          return;
        }
        if (img.complete) {
          img.classList.add('v2-trd-img--loaded');
        }
      });

    this.refreshAccordionHeights();
  }

  private refreshAccordionHeights(): void {
    this.accordionRefs?.forEach((accordion) => accordion.refreshHeight());
  }

  private clearImageLoadSyncTimers(): void {
    for (const timer of this.imageLoadSyncTimers) {
      clearTimeout(timer);
    }
    this.imageLoadSyncTimers = [];
  }

  private scheduleCloseAnimationFinish(): void {
    this.clearCloseAnimationFinishTimer();
    this.closeAnimationFinishTimer = setTimeout(() => {
      this.domOpen = false;
      this.isClosing = false;
      this.setBodyScrollLock(false);
      this.cdr.markForCheck();
    }, V2TradeRequestDetailOverlayComponent.OVERLAY_CLOSE_ANIM_MS);
  }

  private clearCloseAnimationFinishTimer(): void {
    if (this.closeAnimationFinishTimer) {
      clearTimeout(this.closeAnimationFinishTimer);
      this.closeAnimationFinishTimer = null;
    }
  }

  private scheduleInteractionReady(): void {
    this.clearInteractionReadyTimer();
    this.interactionReadyTimer = setTimeout(() => {
      this.interactionReady = true;
      this.interactionReadyTimer = null;
      this.syncCachedOverlayImages();
      this.cdr.markForCheck();
    }, V2TradeRequestDetailOverlayComponent.INTERACTION_GUARD_MS);
  }

  private clearInteractionReadyTimer(): void {
    if (this.interactionReadyTimer) {
      clearTimeout(this.interactionReadyTimer);
      this.interactionReadyTimer = null;
    }
  }

  private resetBodyScroll(): void {
    const el = this.bodyScrollRef?.nativeElement;
    if (el) {
      el.scrollTop = 0;
    }
    this.isAtTop = true;
  }

  private setBodyScrollLock(locked: boolean): void {
    document.body.classList.toggle('v2-trade-request-detail-open', locked);
  }
}
